import { useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Boxes, Printer, ListChecks, Loader2 } from "lucide-react";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle, tableDensity } from "@/components/ops-ui";

const PER_PAGE = 60;
const ROWS = 20;

export default function ReturnToRack() {
  const { can } = useAuth();
  const { density, setDensity } = useUiPrefs("return-to-rack");
  const [raw, setRaw] = useState("");
  const [results, setResults] = useState(null);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!raw.trim()) return toast.error("Paste your SKU list first");
    setLoading(true);
    try {
      const { data } = await api.post("/return-to-rack", { raw });
      setResults(data.results);
      setCounts(data.counts);
      if (!data.results.length) toast.error("No valid SKUs found in the pasted text");
      else toast.success(`${data.counts.total} unique SKU(s) · ${data.counts.not_found} not found`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate rack list");
    } finally { setLoading(false); }
  };

  const pages = useMemo(() => {
    const out = [];
    const list = results || [];
    for (let i = 0; i < list.length; i += PER_PAGE) out.push(list.slice(i, i + PER_PAGE));
    return out;
  }, [results]);

  return (
    <div data-testid="return-to-rack-page">
      <style>{`
        @media print {
          @page { size: A5 landscape; margin: 5mm; }
          body * { visibility: hidden !important; }
          .rtr-print, .rtr-print * { visibility: visible !important; }
          .rtr-print { position: absolute; left: 0; top: 0; width: 100%; }
          .rtr-page { width: 100% !important; min-height: auto !important; border: none !important;
                      box-shadow: none !important; margin: 0 !important; padding: 0 !important;
                      page-break-after: always; }
          .rtr-page:last-child { page-break-after: auto; }
          .no-print { display: none !important; }
        }
        .rtr-page { width: 200mm; min-height: 138mm; background: #fff; border: 1px solid #e2e8f0;
                    margin: 0 auto 14px; padding: 6mm 6mm 4mm; box-sizing: border-box; }
        .rtr-title { text-align: center; font-weight: 800; font-size: 13px; letter-spacing: .04em;
                     margin-bottom: 4px; }
        .rtr-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .rtr-table td, .rtr-table th { padding: 1px 3px; font-size: 9px; line-height: 1.25;
                     overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
        .rtr-table th { border-bottom: 1px solid #94a3b8; font-size: 8px; text-align: left;
                     text-transform: uppercase; color: #475569; }
        .rtr-sku { font-family: ui-monospace, monospace; border-bottom: 1px dotted #e2e8f0; }
        .rtr-rack { font-family: ui-monospace, monospace; font-weight: 700; font-size: 10.5px;
                     border-bottom: 1px dotted #e2e8f0; }
        .rtr-sp { border: none; }
        .rtr-nf { color: #dc2626; font-weight: 700; font-size: 8.5px;
                  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .rtr-pageno { text-align: right; font-size: 8px; color: #64748b; margin-top: 3px; }
        .rtr-colgroup-sku { width: 20%; } .rtr-colgroup-rack { width: 11%; } .rtr-colgroup-sp { width: 2.33%; }
      `}</style>

      <header className="mb-5 no-print flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="caption">Utility</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Return to Rack</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Paste a column of SKUs (returned/cancelled items already checked good). We normalize, remove duplicates,
            match the Master SKU rack code, sort by rack sequence, and produce an A5 rack list to print.
          </p>
        </div>
        <DensityToggle density={density} setDensity={setDensity} />
      </header>

      <div className="grid gap-5 lg:grid-cols-[380px_1fr] no-print">
        <div className="space-y-3">
          <label className="text-sm font-medium">Paste SKU List</label>
          <Textarea
            data-testid="rtr-input"
            value={raw}
            onChange={e => setRaw(e.target.value)}
            placeholder={"WTLPM1524I\nTCUPM2PINK\nWTLPM1524I\nTLPM1BK30INCH\nTCUPM2PINK"}
            className="h-[300px] font-mono text-sm"
          />
          <div className="flex gap-2">
            <Button onClick={generate} disabled={loading} data-testid="rtr-generate" className="bg-slate-900">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ListChecks className="w-4 h-4 mr-1.5" />}
              Generate Rack List
            </Button>
            {results && results.length > 0 && (
              <Button variant="outline" onClick={() => window.print()} data-testid="rtr-print"
                disabled={!can("print:packing_list") && false}>
                <Printer className="w-4 h-4 mr-1.5" />Print Rack List
              </Button>
            )}
          </div>
          {counts && (
            <div className="text-xs text-slate-500" data-testid="rtr-counts">
              {counts.total} unique · <span className="text-emerald-600">{counts.found} found</span>
              {counts.not_found > 0 && <> · <span className="text-red-600">{counts.not_found} not found</span></>}
            </div>
          )}
        </div>

        <div>
          {results && results.length > 0 ? (
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <table className={`excel-table w-full text-sm ${tableDensity(density)}`} data-testid="rtr-results-table">
                <thead><tr>
                  <th className="w-14 text-right">No.</th>
                  <th>SKU</th>
                  <th>Rack Code</th>
                </tr></thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={i} data-testid={`rtr-row-${i}`} className={!r.found ? "bg-red-50" : ""}>
                      <td className="text-right text-slate-400">{i + 1}</td>
                      <td className="font-mono font-medium">{r.sku}</td>
                      <td className={r.found ? "font-mono font-bold" : "font-semibold text-red-600"}
                        data-testid={`rtr-rack-${i}`}>
                        {r.found ? r.rack_code : "SKU NOT FOUND"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-full min-h-[300px] grid place-items-center text-slate-400 border border-dashed border-slate-200 rounded-lg">
              <div className="text-center">
                <Boxes className="w-10 h-10 mx-auto mb-2 opacity-40" />
                Paste SKUs and click Generate Rack List
              </div>
            </div>
          )}
        </div>
      </div>

      {/* A5 print preview — this is exactly what prints */}
      {results && results.length > 0 && (
        <div className="mt-6">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 no-print">Print Preview (A5 Landscape)</div>
          <div className="rtr-print overflow-x-auto" data-testid="rtr-print-area">
            {pages.map((pageItems, pi) => (
              <div className="rtr-page" data-testid={`rtr-page-${pi}`} key={pi}>
                <div className="rtr-title">FASFONE &ndash; RETURN TO RACK</div>
                <table className="rtr-table">
                  <colgroup>
                    <col className="rtr-colgroup-sku" /><col className="rtr-colgroup-rack" /><col className="rtr-colgroup-sp" />
                    <col className="rtr-colgroup-sku" /><col className="rtr-colgroup-rack" /><col className="rtr-colgroup-sp" />
                    <col className="rtr-colgroup-sku" /><col className="rtr-colgroup-rack" /><col className="rtr-colgroup-sp" />
                  </colgroup>
                  <thead><tr>
                    <th>SKU</th><th>Rack</th><th></th>
                    <th>SKU</th><th>Rack</th><th></th>
                    <th>SKU</th><th>Rack</th><th></th>
                  </tr></thead>
                  <tbody>
                    {Array.from({ length: ROWS }).map((_, r) => (
                      <tr key={r}>
                        {[0, 1, 2].map(b => {
                          const it = pageItems[b * ROWS + r];
                          return [
                            <td className="rtr-sku" key={`s${b}`}>{it ? it.sku : ""}</td>,
                            <td className={"rtr-rack" + (it && !it.found ? " rtr-nf" : "")} key={`r${b}`}>
                              {it ? (it.found ? it.rack_code : "SKU NOT FOUND") : ""}
                            </td>,
                            <td className="rtr-sp" key={`p${b}`}></td>,
                          ];
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="rtr-pageno">Page {pi + 1} / {pages.length}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
